import AudioPlayer from '../../../components/AudioPlayer';
import HelpTip from '../../../components/HelpTip';
import Modal from '../../../components/Modal';
import SkillVariableForm from '../../../components/SkillVariableForm';
import { AnalysisMode, PromptSkill, VoicePreset } from '../../../types';
import { Language, LanguageSkill } from '../../../types/language';

type Translate = (key: string, options?: Record<string, unknown>) => string;

interface EditProjectModalProps {
  open: boolean;
  saving: boolean;
  t: Translate;
  editName: string;
  editPrompt: string;
  editRatio: string;
  editSkillId: string;
  editSkillVariableValues: Record<string, string>;
  editLanguageSkillId: string;
  editLanguageSkillValues: Record<string, string>;
  editAutoGenerateShots: boolean;
  editAnalysisMode: AnalysisMode;
  analysisStarted: boolean;
  editAutoStartAnalysis: boolean;
  editEnableVoiceInsertion: boolean;
  editVoicePrompt: string;
  editVoicePresetId: string;
  editDisableBGM: boolean;
  enableScriptNormalization: boolean;
  scriptSkillName: string;
  skills: PromptSkill[];
  langSkills: LanguageSkill[];
  langSkillsLoading: boolean;
  languages: Language[];
  voicePresets: VoicePreset[];
  onClose: () => void;
  onSave: () => Promise<void>;
  setEditName: (value: string) => void;
  setEditPrompt: (value: string) => void;
  setEditRatio: (value: string) => void;
  setEditSkillId: (value: string) => void;
  setEditSkillVariableValues: (value: Record<string, string>) => void;
  setEditOutputLanguage: (value: string) => void;
  setEditLanguageSkillId: (value: string) => void;
  setEditLanguageSkillValues: (value: Record<string, string> | ((previous: Record<string, string>) => Record<string, string>)) => void;
  setEditAutoGenerateShots: (value: boolean) => void;
  setEditAnalysisMode: (value: AnalysisMode) => void;
  setEditAutoStartAnalysis: (value: boolean) => void;
  setEditEnableVoiceInsertion: (value: boolean) => void;
  setEditVoicePrompt: (value: string) => void;
  setEditVoicePresetId: (value: string) => void;
  setEditDisableBGM: (value: boolean) => void;
}

export default function EditProjectModal({
  open,
  saving,
  t,
  editName,
  editPrompt,
  editRatio,
  editSkillId,
  editSkillVariableValues,
  editLanguageSkillId,
  editLanguageSkillValues,
  editAutoGenerateShots,
  editAnalysisMode,
  analysisStarted,
  editAutoStartAnalysis,
  editEnableVoiceInsertion,
  editVoicePresetId,
  editDisableBGM,
  enableScriptNormalization,
  scriptSkillName,
  skills,
  langSkills,
  langSkillsLoading,
  languages,
  voicePresets,
  onClose,
  onSave,
  setEditName,
  setEditPrompt,
  setEditRatio,
  setEditSkillId,
  setEditSkillVariableValues,
  setEditOutputLanguage,
  setEditLanguageSkillId,
  setEditLanguageSkillValues,
  setEditAutoGenerateShots,
  setEditAnalysisMode,
  setEditAutoStartAnalysis,
  setEditEnableVoiceInsertion,
  setEditVoicePrompt,
  setEditVoicePresetId,
  setEditDisableBGM,
}: EditProjectModalProps) {
  const selectedVoicePreset = voicePresets.find(preset => preset.id === editVoicePresetId);
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('editModal.title')}
      width="563px"
      footer={
        <>
          <button className="btn btn-secondary" onClick={onClose} disabled={saving}>{t('cancel', { ns: 'common' })}</button>
          <button className="btn btn-primary" onClick={onSave} disabled={saving || !editName.trim()}>
            {saving ? <><span className="loading-spinner"></span>{t('savingEllipsis', { ns: 'common' })}</> : t('save', { ns: 'common' })}
          </button>
        </>
      }
    >
      <div className="form-group">
        <label className="form-label">{t('editModal.projectName')}</label>
        <input
          type="text"
          className="input"
          value={editName}
          onChange={event => setEditName(event.target.value)}
        />
      </div>
      <div className="form-group">
        <label className="form-label">{t('editModal.projectDesc')}</label>
        <textarea
          className="input textarea"
          rows={1}
          style={{ minHeight: 'unset', resize: 'vertical' }}
          value={editPrompt}
          onChange={event => setEditPrompt(event.target.value)}
          placeholder={t('editModal.projectDescPlaceholder')}
        />
      </div>
      <div className="form-group">
        <label className="form-label">{t('editModal.aspectRatio')}</label>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          {([
            { ratio: '9:16', icon: (
              <svg width="18" height="28" viewBox="0 0 18 28" fill="none" style={{ display: 'block' }}>
                <rect x="1" y="1" width="16" height="26" rx="3" stroke="currentColor" strokeWidth="1.5" fill="none" />
                <line x1="6" y1="25" x2="12" y2="25" stroke="currentColor" strokeWidth="1" strokeLinecap="round" />
              </svg>
            ), label: '9:16' },
            { ratio: '16:9', icon: (
              <svg width="30" height="20" viewBox="0 0 30 20" fill="none" style={{ display: 'block' }}>
                <rect x="1" y="1" width="28" height="18" rx="2" stroke="currentColor" strokeWidth="1.5" fill="none" />
                <line x1="13" y1="16" x2="17" y2="16" stroke="currentColor" strokeWidth="1" strokeLinecap="round" />
              </svg>
            ), label: '16:9' },
            { ratio: '21:9', icon: (
              <svg width="38" height="18" viewBox="0 0 38 18" fill="none" style={{ display: 'block' }}>
                <rect x="1" y="1" width="36" height="16" rx="2" stroke="currentColor" strokeWidth="1.5" fill="none" />
                <line x1="17" y1="14" x2="21" y2="14" stroke="currentColor" strokeWidth="1" strokeLinecap="round" />
              </svg>
            ), label: '21:9' },
          ] as const).map(({ ratio, icon, label }) => (
            <button
              key={ratio}
              type="button"
              onClick={() => setEditRatio(ratio)}
              style={{
                flex: 1, padding: '0.5rem 0.4rem', border: '1px solid',
                borderRadius: '8px', cursor: 'pointer',
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end',
                gap: '0.3rem', minHeight: '56px',
                transition: 'all 0.15s',
                background: editRatio === ratio ? 'var(--primary)' : 'transparent',
                color: editRatio === ratio ? 'white' : 'var(--text-2)',
                borderColor: editRatio === ratio ? 'var(--primary)' : 'var(--border)',
              }}
            >
              <span style={{ flex: 1, display: 'flex', alignItems: 'center' }}>{icon}</span>
              <span style={{ fontSize: '0.75rem', fontWeight: 600 }}>{label}</span>
            </button>
          ))}
        </div>
      </div>
      {skills.length > 0 && (
        <div className="form-group">
          <label className="form-label">{t('editModal.shotSkill')}<HelpTip text={t('editModal.shotSkillTip')} /></label>
          <select
            className="input"
            value={editSkillId}
            onChange={event => {
              setEditSkillId(event.target.value);
              const newSkill = skills.find(skill => skill.id === event.target.value);
              const defaults: Record<string, string> = {};
              (newSkill?.variables || []).forEach(variable => { if (variable.default) defaults[variable.key] = variable.default; });
              setEditSkillVariableValues(defaults);
            }}
            style={{ cursor: 'pointer' }}
          >
            <option value="">{t('editModal.selectShotSkill')}</option>
            {skills.map(skill => {
              const status = skill.status || 'published';
              const suffix = skill.isDefault ? t('editModal.skillDefault') : status === 'draft' ? t('editModal.skillDraft') : status === 'pending_review' ? t('editModal.skillPendingReview') : status === 'unpublished' ? t('editModal.skillUnpublished') : '';
              return (
                <option key={skill.id} value={skill.id}>
                  {skill.name}{suffix}
                </option>
              );
            })}
          </select>
          {(() => {
            const selectedSkill = skills.find(skill => skill.id === editSkillId);
            return selectedSkill?.variables && selectedSkill.variables.length > 0 ? (
              <SkillVariableForm
                variables={selectedSkill.variables}
                values={editSkillVariableValues}
                onChange={setEditSkillVariableValues}
              />
            ) : null;
          })()}
        </div>
      )}
      <div className="form-group">
        <label className="form-label">{t('editModal.scriptRewriteLabel')}</label>
        <div style={{
          padding: '0.5rem 0.75rem',
          borderRadius: '6px',
          fontSize: '0.85rem',
          color: 'var(--text-3)',
          background: 'var(--bg-2)',
          border: '1px solid var(--border)',
        }}>
          {enableScriptNormalization ? scriptSkillName : t('editModal.scriptRewriteNotUsed')}
        </div>
      </div>
      <div className="form-group">
        <label className="form-label">{t('editModal.langSkill')}<HelpTip text={t('editModal.langSkillTip')} /></label>
        <select
          className="input"
          value={editLanguageSkillId}
          onChange={event => {
            const newSkill = langSkills.find(skill => skill.id === event.target.value);
            const defaults: Record<string, string> = {};
            (newSkill?.variables || []).forEach(variable => { if (variable.default) defaults[variable.key] = variable.default; });
            setEditLanguageSkillId(event.target.value);
            setEditOutputLanguage(newSkill?.languageCode || '');
            setEditLanguageSkillValues(defaults);
          }}
          style={{ cursor: 'pointer' }}
        >
          <option value="">{t('editModal.langSkillPlaceholder')}</option>
          {langSkills.map(skill => {
            const lang = languages.find(item => item.code === skill.languageCode);
            const langLabel = lang?.name || skill.languageCode;
            return (
              <option key={skill.id} value={skill.id}>
                {skill.name} · {langLabel}{skill.isDefault ? t('editModal.skillDefault') : ''}
              </option>
            );
          })}
        </select>
        {(() => {
          const selected = langSkills.find(skill => skill.id === editLanguageSkillId);
          const vars = selected?.variables || [];
          if (vars.length === 0) return null;
          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', marginTop: '0.5rem' }}>
              {vars.map(variable => (
                <div key={variable.key}>
                  <label style={{ fontSize: '0.82rem', color: 'var(--text-2)', display: 'block', marginBottom: '0.2rem' }}>
                    {variable.label || variable.key}
                  </label>
                  <input
                    className="input"
                    value={editLanguageSkillValues[variable.key] ?? ''}
                    onChange={event => setEditLanguageSkillValues(previous => ({ ...previous, [variable.key]: event.target.value }))}
                    placeholder={variable.default || ''}
                    style={{ fontSize: '0.85rem' }}
                  />
                </div>
              ))}
            </div>
          );
        })()}
        {!langSkillsLoading && langSkills.length === 0 && (
          <p style={{ margin: '0.25rem 0 0', fontSize: '0.75rem', color: 'var(--text-3)' }}>
            {t('editModal.noLangSkills')}
          </p>
        )}
      </div>
      <div className="form-group">
        <label className="form-label">{t('editModal.analysisMode')}{analysisStarted && <span style={{ fontSize: '0.75rem', color: 'var(--text-3)', marginLeft: '0.35rem' }}>{t('editModal.analysisModeLockedHint')}</span>}</label>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'stretch' }}>
          {([
            { value: 'external' as const, title: t('editModal.analysisModeExternal'), desc: t('editModal.analysisModeExternalDesc') },
            { value: 'advanced' as const, title: t('editModal.analysisModeAdvanced'), desc: t('editModal.analysisModeAdvancedDesc') },
            { value: 'basic' as const, title: t('editModal.analysisModeBasic'), desc: t('editModal.analysisModeBasicDesc') },
          ]).map(({ value, title, desc }) => (
            <button
              key={value}
              type="button"
              disabled={analysisStarted}
              onClick={() => setEditAnalysisMode(value)}
              style={{
                flex: 1, padding: '0.45rem 0.5rem', border: '1px solid',
                borderRadius: '6px', cursor: analysisStarted ? 'not-allowed' : 'pointer',
                display: 'flex', alignItems: 'center', gap: '0.4rem',
                transition: 'all 0.15s',
                opacity: analysisStarted ? 0.5 : 1,
                background: editAnalysisMode === value ? 'var(--primary)' : 'transparent',
                color: editAnalysisMode === value ? 'white' : 'var(--text-2)',
                borderColor: editAnalysisMode === value ? 'var(--primary)' : 'var(--border)',
              }}
            >
              <span style={{ fontSize: '0.85rem', fontWeight: 600, flexShrink: 0, width: '2em', lineHeight: 1.4, textAlign: 'center' }}>{title}</span>
              <span style={{ fontSize: '0.65rem', opacity: 0.7, textAlign: 'right', flex: 1, whiteSpace: 'pre-line', lineHeight: 1.4 }}>{desc}</span>
            </button>
          ))}
        </div>
      </div>
      <div className="form-group">
        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.875rem' }}>
          <input type="checkbox" checked={editAutoStartAnalysis} onChange={event => setEditAutoStartAnalysis(event.target.checked)} />
          {t('editModal.autoStartAnalysis')}
          <HelpTip text={t('editModal.autoStartAnalysisTip')} />
        </label>
      </div>
      <div className="form-group">
        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.875rem' }}>
          <input
            type="checkbox"
            checked={editEnableVoiceInsertion}
            onChange={event => setEditEnableVoiceInsertion(event.target.checked)}
          />
          {t('editModal.enableVoiceInsertion')}
          <HelpTip text={t('editModal.enableVoiceInsertionTip')} />
        </label>
      </div>
      {editEnableVoiceInsertion && voicePresets.length > 0 && (
        <div className="form-group">
          <label className="form-label" style={{ fontSize: '0.85rem' }}>{t('editModal.voicePreset')}</label>
          <select
            className="input"
            value={editVoicePresetId}
            onChange={event => {
              const preset = voicePresets.find(item => item.id === event.target.value);
              setEditVoicePresetId(event.target.value);
              setEditVoicePrompt(preset ? preset.prompt : '');
            }}
          >
            <option value="">{t('editModal.voicePresetPlaceholder')}</option>
            {voicePresets.map(preset => (
              <option key={preset.id} value={preset.id}>{preset.name}</option>
            ))}
          </select>
          {selectedVoicePreset?.audioUrl && (
            <div style={{ marginTop: '0.5rem' }}>
              <AudioPlayer src={selectedVoicePreset.audioUrl} maxWidth="100%" />
            </div>
          )}
        </div>
      )}
      <div className="form-group">
        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.875rem' }}>
          <input
            type="checkbox"
            checked={editDisableBGM}
            onChange={event => setEditDisableBGM(event.target.checked)}
          />
          {t('editModal.disableBGM')}
          <HelpTip text={t('editModal.disableBGMTip')} />
        </label>
      </div>
    </Modal>
  );
}
